/**
 * FilterSection コンポーネント
 *
 * FilterPanel 内で繰り返し使われるフィルタセクション（グループ）の共通 UI。
 * タイトルと複数のトグルボタンを描画する。
 */

import { cn } from '@/lib/cn';

export interface FilterSectionProps {
  /** aria-labelledby 用のタイトル要素 ID */
  titleId: string;

  /** セクション見出し */
  title: string;

  /** 選択肢の一覧 */
  items: string[];

  /** 現在選択中のアイテム */
  selectedItems: string[];

  /** アイテムがトグルされたときのコールバック */
  onToggle: (item: string) => void;

  /**
   * ボタンの追加クラスを返す関数
   * @param item - ボタンのラベル
   * @param isSelected - 選択中かどうか
   */
  getButtonClass: (item: string, isSelected: boolean) => string;
}

/**
 * FilterSection コンポーネント
 *
 * 使用例:
 * ```tsx
 * <FilterSection
 *   titleId="genre-filter-title"
 *   title={t('filter.genre')}
 *   items={availableGenres}
 *   selectedItems={localGenres}
 *   onToggle={handleGenreToggle}
 *   getButtonClass={(genre, isSelected) =>
 *     getGenreBackgroundClass(genre, isSelected)
 *   }
 * />
 * ```
 */
export function FilterSection({
  titleId,
  title,
  items,
  selectedItems,
  onToggle,
  getButtonClass,
}: FilterSectionProps) {
  if (items.length === 0) return null;

  return (
    <div className="mb-6" role="group" aria-labelledby={titleId}>
      <h3 id={titleId} className="mb-3 font-semibold">
        {title}
      </h3>
      <div className="flex flex-wrap gap-2">
        {items.map((item) => {
          const isSelected = selectedItems.includes(item);
          return (
            <button
              key={item}
              onClick={() => onToggle(item)}
              aria-pressed={isSelected}
              className={cn(
                'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
                getButtonClass(item, isSelected)
              )}
            >
              {item}
            </button>
          );
        })}
      </div>
    </div>
  );
}
